import { BrandMark } from "./BrandMark.tsx";
import { Glass } from "./Glass.tsx";
import { SiteFooter } from "./SiteFooter.tsx";
import { SkyCanvas } from "./SkyCanvas.tsx";
import {
  GITHUB_REPO_URL,
  QQ_CHANNEL_URL,
  SITE_NAME,
  SPONSOR_URL,
} from "../lib/site.ts";

const plans = [
  {
    id: "annual",
    title: "官方年度授权",
    price: "¥9.9 / 年",
    body: "适合先使用官方发行版一年。授权绑定单台设备，激活后授权校验和日常提醒均可离线完成。",
    features: ["单设备授权 1 年", "官方发行版自动更新", "适合先长期体验"],
  },
  {
    id: "lifetime",
    title: "官方终身授权",
    price: "¥29.9",
    body: "适合同一台常用电脑长期使用。一次购买长期有效，仍然使用官方发行版和官方更新链路。",
    features: ["单设备长期授权", "官方发行版自动更新", "激活后离线可用"],
  },
];

const supportCards = [
  {
    title: "源码自构建",
    body: "源码按 Apache-2.0 开放，可以查看、修改、自构建，也可以自行替换授权公钥、更新地址和品牌入口。",
    link: GITHUB_REPO_URL,
    label: "查看 GitHub 仓库",
  },
  {
    title: "赞助项目",
    body: "赞助是自愿支持维护，不等同于购买授权。它不会改变开源许可，也不会自动生成官方授权码。",
    link: SPONSOR_URL,
    label: "前往赞助",
  },
];

function usePlanHighlight() {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("plan") ?? "";
}

export function PurchasePage() {
  const plan = usePlanHighlight();

  return (
    <div className="relative isolate min-h-dvh overflow-x-clip bg-background text-foreground">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-20"
        style={{
          backgroundImage: "radial-gradient(oklch(1 0 0 / 0.1) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
      />

      <div className="pointer-events-none fixed inset-x-0 top-0 z-40 px-3 pt-3 sm:px-4 sm:pt-4">
        <header className="pointer-events-auto relative mx-auto h-14 w-full max-w-6xl rounded-2xl">
          <div aria-hidden="true" className="absolute inset-0">
            <Glass className="h-full rounded-2xl border border-border/50 shadow-lg shadow-black/10" />
          </div>
          <div className="relative z-10 flex h-full items-center gap-3 px-3 sm:gap-4 sm:px-4">
            <a
              href="/"
              className="flex shrink-0 items-center gap-2.5 text-foreground transition-opacity hover:opacity-80"
            >
              <BrandMark className="size-7" />
              <span className="text-[15px] font-semibold tracking-tight">{SITE_NAME}</span>
            </a>
            <div className="ml-auto flex items-center gap-2">
              <a
                href="/#pricing"
                className="hidden rounded-full px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground sm:inline-flex"
              >
                返回价格
              </a>
              <a
                href={QQ_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-9 items-center rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground"
              >
                加入 QQ 频道
              </a>
            </div>
          </div>
        </header>
      </div>

      <div aria-hidden="true" className="h-[68px] sm:h-[72px]" />

      <main className="mx-auto w-full max-w-6xl px-6 pb-16 pt-10 sm:pt-14">
        <div className="mx-auto max-w-3xl text-center">
          <p className="font-mono text-xs tracking-wide text-muted-foreground/70">License</p>
          <h1 className="mt-4 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
            购买官方发行版授权
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            源码已开源；官方发行版继续保留年度与终身授权，用于支持官方构建、自动更新和既有授权兼容。
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="/#pricing"
              className="inline-flex h-11 items-center justify-center rounded-full border border-white/15 bg-background/25 px-6 text-sm font-medium text-foreground/90"
            >
              返回价格与下载
            </a>
            <a
              href={QQ_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground"
            >
              加入 QQ 频道
            </a>
          </div>
        </div>

        <div className="relative mt-14 grid gap-4 lg:grid-cols-2">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-3xl opacity-60"
            style={{
              maskImage: "linear-gradient(to bottom, black, transparent 90%)",
              WebkitMaskImage: "linear-gradient(to bottom, black, transparent 90%)",
            }}
          >
            <SkyCanvas mode="mist" className="h-full w-full -scale-y-100" />
          </div>
          {plans.map((item) => {
            const active = plan === item.id;
            return (
              <article
                key={item.id}
                id={item.id}
                className={`rounded-2xl border p-7 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] ${
                  active
                    ? "border-white/25 bg-[oklch(0.19_0.005_285_/_0.96)]"
                    : "border-white/[0.08] bg-[oklch(0.17_0.005_285_/_0.92)]"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <h2 className="text-xl font-semibold tracking-tight">{item.title}</h2>
                  <span className="rounded-full bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground">
                    {item.price}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                <ul className="mt-5 flex flex-col gap-2 text-sm text-muted-foreground">
                  {item.features.map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-foreground/40" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <a
                  href={QQ_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex h-11 items-center justify-center rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground"
                >
                  通过 QQ 频道购买
                </a>
              </article>
            );
          })}
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {supportCards.map((card) => (
            <article
              key={card.title}
              className="rounded-2xl border border-white/[0.08] bg-[oklch(0.17_0.005_285_/_0.92)] p-7 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]"
            >
              <h2 className="text-xl font-semibold tracking-tight">{card.title}</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
              <a
                href={card.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex h-11 items-center justify-center rounded-full border border-white/15 bg-background/25 px-6 text-sm font-medium text-foreground/90"
              >
                {card.label}
              </a>
            </article>
          ))}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
